// src/pages/financeiro/ParcelaPagamentosModal.js
import React, { useEffect, useState } from 'react';
import Modal from '../../components/modalForm/StackedBootstrapModal';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { useMessage } from '../../contexts/geral/MessageContext';
import {
    excluirPagamentoRecebimento,
    excluirParcelaRecebimento,
} from '../../services/financeiro/RecebimentoService';
import { obterClasseStatusRecebimento, obterDescricaoStatusRecebimento } from '../../models/financeiro/Recebimento';

const dataHora = (valor) => valor ? `${formatDate(valor)} ${formatTime(valor)}` : '-';

const ParcelaPagamentosModal = ({ show, parcela, onHide, onRecebimentoAtualizado, onParcelaExcluida }) => {
    const { showMessage } = useMessage();
    const [acao, setAcao] = useState(null);
    const [justificativa, setJustificativa] = useState('');
    const [processando, setProcessando] = useState(false);

    useEffect(() => {
        if (!show) return;
        setAcao(null);
        setJustificativa('');
        setProcessando(false);
    }, [show, parcela]);

    if (!parcela) return null;
    const pagamentosAtivos = (parcela.pagamentos || []).filter((pagamento) => !pagamento.excluido);

    const confirmar = async () => {
        const motivo = justificativa.trim();
        if (!motivo) return showMessage('Informe a justificativa.', 'warning');
        setProcessando(true);
        try {
            if (acao?.tipo === 'pagamento') {
                const atualizado = await excluirPagamentoRecebimento(acao.pagamento.id, motivo);
                await onRecebimentoAtualizado?.(atualizado);
                showMessage('Pagamento removido e saldos recalculados.', 'success');
            } else if (acao?.tipo === 'parcela') {
                await excluirParcelaRecebimento(parcela.id, motivo);
                await onParcelaExcluida?.();
                onHide();
                showMessage('Parcela excluída com sucesso.', 'success');
            }
            setAcao(null);
            setJustificativa('');
        } catch (error) {
            showMessage(error?.message || 'Não foi possível concluir a operação.', 'danger');
        } finally {
            setProcessando(false);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="xl" className="historico-pagamentos-modal">
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>Detalhes da parcela {parcela.numParcela}</Modal.Title>
                        <small className="text-muted">Vencimento em {formatDate(parcela.dataVencimento)}</small>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <div className="parcela-resumo mb-3">
                        <div><span>Valor original</span><strong>{formatCurrency(parcela.valorParcela)}</strong></div>
                        <div><span>Valor pago</span><strong className="text-success">{formatCurrency(parcela.valorPago)}</strong></div>
                        <div><span>Saldo</span><strong>{formatCurrency(parcela.saldo)}</strong></div>
                        <div><span>Status</span><strong className={`status-badge ${obterClasseStatusRecebimento(parcela.statusRec)}`}>{obterDescricaoStatusRecebimento(parcela.statusRec)}</strong></div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Histórico de pagamentos</h6>
                        <span className="text-muted small">{pagamentosAtivos.length} pagamento(s) ativo(s)</span>
                    </div>
                    <div className="table-responsive historico-pagamentos-grid">
                        <table className="table table-sm table-hover align-middle mb-0">
                            <thead><tr><th>Data do pagamento</th><th>Forma</th><th>Registrado por</th><th>Registrado em</th><th className="text-end">Valor</th><th>Situação</th><th></th></tr></thead>
                            <tbody>
                                {(parcela.pagamentos || []).map((pagamento) => (
                                    <tr key={pagamento.id} className={pagamento.excluido ? 'pagamento-excluido' : ''}>
                                        <td>{dataHora(pagamento.dataPagamento)}</td>
                                        <td>{pagamento.formaPagamentoDescricao || 'Não informada'}</td>
                                        <td>{pagamento.usuarioNome || (pagamento.usuarioId ? `Usuário #${pagamento.usuarioId}` : 'Migração')}</td>
                                        <td>{dataHora(pagamento.dataCriacao)}</td>
                                        <td className="text-end fw-bold">{formatCurrency(pagamento.valorPagamento)}</td>
                                        <td>
                                            {pagamento.excluido
                                                ? <span className="badge bg-secondary">Excluído</span>
                                                : <span className="badge bg-success">Ativo</span>}
                                        </td>
                                        <td className="text-end">
                                            {!pagamento.excluido && (
                                                <button type="button" className="btn btn-sm btn-outline-danger"
                                                    onClick={() => { setAcao({ tipo: 'pagamento', pagamento }); setJustificativa(''); }}>
                                                    Remover
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!(parcela.pagamentos || []).length && (
                                    <tr><td colSpan="7" className="text-center text-muted py-4">Nenhum pagamento registrado nesta parcela.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {(parcela.pagamentos || []).filter((pagamento) => pagamento.excluido).map((pagamento) => (
                        <div className="pagamento-exclusao-info" key={`exclusao-${pagamento.id}`}>
                            <strong>Pagamento #{pagamento.id} removido em {dataHora(pagamento.dataExclusao)}</strong>
                            <span>por {pagamento.usuarioExclusaoNome || `usuário #${pagamento.usuarioExclusao || '-'}`}: {pagamento.justificativaExclusao}</span>
                        </div>
                    ))}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <button type="button" className="btn btn-outline-danger"
                        disabled={pagamentosAtivos.length > 0}
                        title={pagamentosAtivos.length ? 'Remova os pagamentos ativos antes de excluir a parcela.' : undefined}
                        onClick={() => { setAcao({ tipo: 'parcela' }); setJustificativa(''); }}>
                        Excluir parcela
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={onHide}>Fechar</button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!acao} onHide={processando ? undefined : () => setAcao(null)} centered className="confirmacao-exclusao-modal">
                <Modal.Header closeButton={!processando}>
                    <Modal.Title>{acao?.tipo === 'pagamento' ? 'Remover pagamento' : 'Excluir parcela'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-warning py-2">
                        {acao?.tipo === 'pagamento'
                            ? 'O pagamento será removido logicamente e os saldos serão recalculados.'
                            : 'A parcela será excluída logicamente do recebimento.'}
                    </div>
                    <label className="form-label">Justificativa</label>
                    <textarea className="form-control" rows="3" maxLength="1000" value={justificativa}
                        onChange={(event) => setJustificativa(event.target.value)} disabled={processando} autoFocus />
                    <div className="text-end text-muted small mt-1">{justificativa.length}/1000</div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setAcao(null)} disabled={processando}>Cancelar</button>
                    <button className="btn btn-danger" onClick={confirmar} disabled={processando || !justificativa.trim()}>
                        {processando ? 'Processando...' : 'Confirmar'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ParcelaPagamentosModal;
